import { get } from 'lodash';

import { SPOT_STATUSES_RESERVED, SpotStatus } from '@nova/core';
import type { ISpot } from '@nova/core';
import type { SpotGridItem } from '@satellite/composables/useYardSpotGridPresenterV2';
import type { SpotGridOpenMoveTaskRef } from '@satellite/composables/yard/spotGridTypes';
import type { YardTaskDetails } from '@satellite/composables/useYardTaskPresenterV2';

/**
 * Slim open MOVE yard task payload from yard-view BFF responses (spot grid, list rows, spot detail).
 * Same shape as {@link SpotGridOpenMoveTaskRef}; use this name when the consumer is not spot-grid-specific.
 */
export type YardMoveTaskHint = SpotGridOpenMoveTaskRef;

export function isSpotGridItem(input: ISpot | SpotGridItem): input is SpotGridItem {
  return (
    input !== null &&
    typeof input === 'object' &&
    'openMoveTasks' in input &&
    'spotAssignments' in input &&
    Array.isArray((input as SpotGridItem).spotAssignments) &&
    !('spot' in input)
  );
}

export function getSpotId(spot?: ISpot | SpotGridItem) {
  if (isSpotGridItem(spot)) {
    return spot.id;
  }

  return spot?.id ?? null;
}

export function toISpot(spot: ISpot | SpotGridItem): ISpot {
  if (isSpotGridItem(spot)) {
    return {
      id: spot.id,
      code: spot.code,
      type: spot.type as ISpot['type'],
      isActive: spot.isActive,
      status: spot.status,
      spotAssignments: spot.spotAssignments as unknown as ISpot['spotAssignments'],
      spotReserves: spot.spotReserves as unknown as ISpot['spotReserves'],
      openMoveTasks: spot.openMoveTasks ?? []
    } as unknown as ISpot;
  }

  return spot;
}

export interface UseYardSpotStatusOptions {
  // eslint-disable-next-line no-unused-vars
  getActiveMoveTasksForSpot?: (
    input?: ISpot | SpotGridItem
  ) => Array<YardTaskDetails | YardMoveTaskHint>;
  // eslint-disable-next-line no-unused-vars
  resolveSpotStatus?: (input: ISpot | SpotGridItem) => SpotStatus;
}

export function useYardSpotStatus(options: UseYardSpotStatusOptions = {}) {
  const defaultGetActiveMoveTasksForSpot = (
    spot?: ISpot | SpotGridItem
  ): Array<YardTaskDetails | YardMoveTaskHint> => {
    const spotId = getSpotId(spot);
    if (!spotId) {
      return [];
    }

    if (isSpotGridItem(spot)) {
      return spot.openMoveTasks ?? [];
    }

    return [];
  };

  const getSpotStatus =
    options.resolveSpotStatus ?? ((spot: ISpot | SpotGridItem): SpotStatus => toISpot(spot).status);

  const getActiveMoveTasksForSpot =
    options.getActiveMoveTasksForSpot ?? defaultGetActiveMoveTasksForSpot;

  const isSpotOpen = (spot: ISpot | SpotGridItem): boolean => {
    return getSpotStatus(spot) === SpotStatus.OPEN;
  };

  const isSpotClosed = (spot: ISpot | SpotGridItem): boolean => {
    return getSpotStatus(spot) === SpotStatus.CLOSED;
  };

  const isSpotAssigned = (spot: ISpot | SpotGridItem): boolean =>
    getSpotStatus(spot) === SpotStatus.ASSIGNED;

  const isSpotReserved = (spot: ISpot | SpotGridItem): boolean =>
    SPOT_STATUSES_RESERVED.includes(getSpotStatus(spot));

  const isSpotInTransit = (spot: ISpot | SpotGridItem): boolean =>
    getSpotStatus(spot) === SpotStatus.IN_TRANSIT;

  const isOriginOfActiveMoveTask = (spot: ISpot | SpotGridItem): boolean => {
    const spotId = getSpotId(spot);
    if (!spotId) {
      return false;
    }

    if (isSpotGridItem(spot)) {
      return (spot.openMoveTasks ?? []).some(task => task.role === 'origin');
    }

    const activeTasks = getActiveMoveTasksForSpot(spot);
    return activeTasks.some(task => {
      const originSpotId =
        get(task, 'child.originSpotId') || get(task, 'child.originSpot.id') || null;
      return originSpotId === spotId;
    });
  };

  const isDestinationOfActiveMoveTask = (spot: ISpot | SpotGridItem): boolean => {
    const spotId = getSpotId(spot);
    if (!spotId) {
      return false;
    }

    if (isSpotGridItem(spot)) {
      return (spot.openMoveTasks ?? []).some(task => task.role === 'destination');
    }

    return getActiveMoveTasksForSpot(spot).some(task => {
      const destinationSpotId =
        get(task, 'child.destinationSpotId') || get(task, 'child.destinationSpot.id') || null;

      return destinationSpotId === spotId;
    });
  };

  const isSpotInActiveMoveTask = (spot: ISpot | SpotGridItem): boolean => {
    const spotId = getSpotId(spot);
    if (!spotId) {
      return false;
    }

    if (isSpotGridItem(spot)) {
      return (spot.openMoveTasks ?? []).length > 0;
    }

    return Boolean(getActiveMoveTasksForSpot(spot).length);
  };

  /**
   * Returns the raw spot status, disregarding any active move tasks or reservations.
   */
  const getRawStatus = (spot: ISpot | SpotGridItem): SpotStatus => {
    if (isSpotOpen(spot)) {
      return SpotStatus.OPEN;
    }

    if (isSpotClosed(spot)) {
      return SpotStatus.CLOSED;
    }

    if (isSpotAssigned(spot)) {
      return SpotStatus.ASSIGNED;
    }

    if (isSpotReserved(spot)) {
      return getSpotStatus(spot);
    }

    return SpotStatus.UNKNOWN;
  };

  return {
    getRawStatus,
    isSpotOpen,
    isSpotClosed,
    isSpotAssigned,
    isSpotReserved,
    isSpotInTransit,
    isOriginOfActiveMoveTask,
    isDestinationOfActiveMoveTask,
    isSpotInActiveMoveTask,
    getActiveMoveTasksForSpot
  };
}
